import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fdt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";

const TONE = { pending: "bg-amber-50 text-amber-700", approved: "bg-sky-50 text-sky-700", completed: "bg-emerald-50 text-emerald-700",
  rejected: "bg-rose-50 text-rose-700", expired: "bg-slate-100 text-slate-500" };
const LABEL = { approved: "Temporary password set" };

export function PasswordResetRequests() {
  const qc = useQueryClient();
  const [approve, setApprove] = useState(null);
  const [pwd, setPwd] = useState("");
  const { data } = useQuery({ queryKey: ["pwd-reset-requests"], refetchInterval: 60000, queryFn: async () => (await api.get("/team/password-reset-requests")).data });
  const done = (msg) => { qc.invalidateQueries({ queryKey: ["pwd-reset-requests"] }); qc.invalidateQueries({ queryKey: ["team"] }); toast.success(msg); };
  const ok = useMutation({ mutationFn: () => api.post(`/team/password-reset-requests/${approve.id}/approve`, { password: pwd }),
    onSuccess: () => { setApprove(null); setPwd(""); done("Temporary password set. Share it privately; it expires in 24 hours."); }, onError: (e) => toast.error(apiErr(e)) });
  const no = useMutation({ mutationFn: (id) => api.post(`/team/password-reset-requests/${id}/reject`, { reason: "" }),
    onSuccess: () => done("Request rejected. The password was not changed."), onError: (e) => toast.error(apiErr(e)) });
  const items = data?.items || [];
  if (!items.length) return null;
  return (
    <div className="mb-6 rounded-2xl border border-pawkt-border bg-white p-4" data-testid="pwd-reset-requests">
      <p className="mb-2 flex items-center gap-2 font-display font-bold text-pawkt-text"><KeyRound className="h-4 w-4 text-amber-500" /> Password Reset Requests
        {data.pending > 0 && <span className="rounded-full bg-amber-100 px-2 text-xs text-amber-800" data-testid="pwd-reset-pending-count">{data.pending} pending</span>}</p>
      <div className="divide-y divide-slate-100">{items.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm" data-testid={`pwd-req-${r.id}`}>
          <div className="min-w-0">
            <p className="font-semibold text-slate-900">{r.name} <span className="font-normal text-slate-500">· {r.email}</span></p>
            <p className="text-xs capitalize text-slate-500">{r.role}{r.staff_name ? ` · profile: ${r.staff_name}` : ""} · requested {fdt(r.requested_at)}{r.request_count > 1 ? ` (${r.request_count}×)` : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`rounded px-2 py-0.5 text-[11px] font-semibold capitalize ${TONE[r.status]}`} data-testid={`pwd-req-status-${r.id}`}>{LABEL[r.status] || r.status}</span>
            {r.status === "pending" && <>
              <Button size="sm" onClick={() => { setApprove(r); setPwd(""); }} data-testid={`pwd-req-approve-${r.id}`} className="h-8 bg-slate-900 text-white">Approve</Button>
              <Button size="sm" variant="outline" onClick={() => no.mutate(r.id)} disabled={no.isPending} data-testid={`pwd-req-reject-${r.id}`} className="h-8">Reject</Button>
            </>}
          </div>
        </div>))}</div>
      <Dialog open={!!approve} onOpenChange={(v) => !v && setApprove(null)}>
        <DialogContent data-testid="pwd-req-approve-dialog">
          <DialogHeader><DialogTitle>Set temporary password · {approve?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input type="password" autoComplete="new-password" placeholder="Temporary password (min 8 characters)" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="pwd-req-temp-input" />
            <p className="text-xs text-slate-500">Tell {approve?.name} this password privately, in person or by phone. It is never shown again and expires in 24 hours. They must change it when they sign in.</p>
            <Button onClick={() => ok.mutate()} disabled={ok.isPending || pwd.length < 8} data-testid="pwd-req-temp-save" className="w-full bg-slate-900 text-white">Set temporary password</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
